import React, { useCallback, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { get, put, post } from "aws-amplify/api";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import RefreshButton from "components/RefreshButton";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import SLMBrowsePager from "containers/slm/shared/SLMBrowsePager";
import "containers/common/Detail.css";

function SLMAddressDetail({ isAuthenticated, user, streetTypes = [] }) {
  // React Router v6 hooks
  const { id: addressId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Loading and UI state
  const [isLoading, setIsLoading] = useState(true);
  const [showAlert, showAlertState] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modified, setModified] = useState(false);

  // Address ID and navigation
  const [idList, setIdList] = useState([]);
  const [leaseId, setLeaseId] = useState(null);
  const [saleId, setSaleId] = useState(null);
  const [addressRecordId, setAddressRecordId] = useState(null);

  // Address form fields
  const [addressKey, setAddressKey] = useState(null);
  const [hardSequence, setHardSequence] = useState(null);
  const [buildingName, setBuildingName] = useState("");
  const [streetAddressFull, setStreetAddressFull] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [originStreetAddress, setOriginStreetAddress] = useState("");
  const [floorUnit, setFloorUnit] = useState("");
  const [streetNoLow, setStreetNoLow] = useState("");
  const [streetNoLowSuffix, setStreetNoLowSuffix] = useState("");
  const [streetNoHigh, setStreetNoHigh] = useState("");
  const [streetNoHighSuffix, setStreetNoHighSuffix] = useState("");
  const [streetName, setStreetName] = useState("");
  const [extension, setExtension] = useState("");
  const [extensionLabel, setExtensionLabel] = useState("");
  const [cornerStreetName, setCornerStreetName] = useState("");
  const [cornerExtension, setCornerExtension] = useState("");
  const [cornerExtensionLabel, setCornerExtensionLabel] = useState("");
  const [suburb, setSuburb] = useState("");
  const [suburbId, setSuburbId] = useState(null);
  const [stateCode, setStateCode] = useState("");
  const [postcode, setPostcode] = useState("");
  const [subRegionName, setSubRegionName] = useState("");
  const [regionName, setRegionName] = useState("");
  const [cityscopeReference, setCityscopeReference] = useState("");
  const [lotPlan, setLotPlan] = useState("");
  const [titleReference, setTitleReference] = useState("");
  const [createdDate, setCreatedDate] = useState(null);
  const [createdBy, setCreatedBy] = useState("");
  const [lastEditedDate, setLastEditedDate] = useState(null);
  const [lastEditedBy, setLastEditedBy] = useState("");
  const [archivedDate, setArchivedDate] = useState(null);

  // Dropdown state
  const [suburbOptions, setSuburbOptions] = useState([]);
  const [selectedSuburb, setSelectedSuburb] = useState(null);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [selectedCornerExtension, setSelectedCornerExtension] = useState(null);
  const [sales, setSales] = useState([]);
  const [leases, setLeases] = useState([]);

  const hydrateBrowseState = (list, currentAddressId) => {
    const numericAddressId = Number(currentAddressId);

    if (!Array.isArray(list) || list.length === 0 || !Number.isFinite(numericAddressId)) {
      setIdList([]);
      return;
    }

    const normalizedList = list.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    setIdList(normalizedList);
  };

  const retrieveAddress = useCallback(async () => {
    try {
      const response = await get({
        apiName: "colin",
        path: `/slmaddresses/${addressId}`,
      }).response;

      const data = await response.body.json();

      // Populate all fields from response
      setAddressRecordId(data.address_id || data.id || null);
      setAddressKey(data.address_key);
      setHardSequence(data.hard_sequence);
      setBuildingName(data.building_name || "");
      setStreetAddressFull(data.street_address_full || "");
      setStreetAddress(data.street_address || "");
      setOriginStreetAddress(data.origin_street_address || "");
      setFloorUnit(data.floor_unit || "");
      setStreetNoLow(data.street_no_low || "");
      setStreetNoLowSuffix(data.street_no_low_suffix || "");
      setStreetNoHigh(data.street_no_high || "");
      setStreetNoHighSuffix(data.street_no_high_suffix || "");
      setStreetName(data.street_name || "");
      setExtension(data.extension || "");
      setExtensionLabel(data.extension_label || "");
      setCornerStreetName(data.corner_street_name || "");
      setCornerExtension(data.corner_extension || "");
      setCornerExtensionLabel(data.corner_extension_label || "");
      setSuburb(data.suburb || "");
      setSuburbId(data.suburb_id);
      setStateCode(data.state_code || "");
      setPostcode(data.postcode || "");
      setSubRegionName(data.sub_region_name || "");
      setRegionName(data.region_name || "");
      setCityscopeReference(data.cityscope_reference || "");
      setLotPlan(data.lot_plan || "");
      setTitleReference(data.title_reference || "");
      setCreatedDate(data.created_date);
      setCreatedBy(data.created_by);
      setLastEditedDate(data.last_edited_date);
      setLastEditedBy(data.last_edited_by);
      setArchivedDate(data.archived_date);

      // Set selected dropdown values
      if (data.state_code) {
        await loadSuburbs({ state: data.state_code });
      }
      if (data.suburb_id) {
        setSelectedSuburb({ value: data.suburb_id, label: `${data.suburb} ${data.postcode}` });
      }
      if (data.extension) {
        setSelectedExtension({ value: data.extension, label: data.extension_label });
      }
      if (data.corner_extension) {
        setSelectedCornerExtension({ value: data.corner_extension, label: data.corner_extension_label });
      }
    } catch (error) {
      throw error;
    }
  }, [addressId]);

  // Load address on mount or addressId change
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        setIsLoading(true);

        if (addressId === "new") {
          // New address form
          setAddressRecordId(null);
          setCreatedDate(new Date());
          setCreatedBy(user);
          setSales([]);
          setLeases([]);
          setModified(false);
        } else {
          // Load existing address
          await retrieveAddress();
          await Promise.all([loadSales(addressId), loadLeases(addressId)]);

          // Extract navigation list from location state or query string
          if (location?.state?.data) {
            hydrateBrowseState(location.state.data, addressId);
          }

          if (location?.state?.lease_id) {
            setLeaseId(location.state.lease_id);
          }
          if (location?.state?.sale_id) {
            setSaleId(location.state.sale_id);
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error loading address:", error);
        setErrorMessage(error.message || "Error loading address");
        setIsLoading(false);
      }
    })();

    document.title = addressId === "new" ? "New Address" : "Address Details";
  }, [addressId, isAuthenticated, user, location, retrieveAddress]);

  const loadSuburbs = async (filter) => {
    try {
      const response = await get({
        apiName: "colin",
        path: "/slmsuburbs",
        options: { queryParams: filter },
      }).response;
      const suburbs = await response.body.json();
      setSuburbOptions(suburbs || []);
    } catch (error) {
      console.error("Error loading suburbs:", error);
    }
  };

  const loadSales = async (id) => {
    try {
      const response = await get({
        apiName: "colin",
        path: "/slmsales",
        options: { queryParams: { addressid: id } },
      }).response;
      const list = await response.body.json();
      setSales(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error loading sales:", error);
      setSales([]);
    }
  };

  const loadLeases = async (id) => {
    try {
      const response = await get({
        apiName: "colin",
        path: "/slmleases",
        options: { queryParams: { addressid: id } },
      }).response;
      const list = await response.body.json();
      setLeases(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error loading leases:", error);
      setLeases([]);
    }
  };

  const refreshAddressMatch = async () => {
    const effectiveAddressId = addressRecordId || addressId;

    if (!effectiveAddressId || effectiveAddressId === "new") {
      return;
    }

    try {
      await put({
        apiName: "colin",
        path: `/slmsales/integrations/related/${effectiveAddressId}`,
        options: { body: effectiveAddressId },
      }).response;
    } catch (error) {
      console.error("Error refreshing address match:", error);
      setErrorMessage(error.message || "Error refreshing address match");
      showAlertState(true);
    }
  };

  const reloadTransactions = async () => {
    const effectiveAddressId = addressRecordId || addressId;

    if (!effectiveAddressId || effectiveAddressId === "new") {
      return;
    }

    await Promise.all([loadSales(effectiveAddressId), loadLeases(effectiveAddressId)]);
  };

  const handleChange = (event) => {
    const { id, value } = event.target;

    switch (id) {
      case "buildingName":
        setBuildingName(value);
        break;
      case "streetNoLow":
        setStreetNoLow(value);
        if (!streetNoHigh || parseInt(streetNoHigh) < parseInt(value)) {
          setStreetNoHigh(value);
        }
        break;
      case "streetNoLowSuffix":
        setStreetNoLowSuffix(value);
        break;
      case "streetNoHigh":
        setStreetNoHigh(value);
        break;
      case "streetNoHighSuffix":
        setStreetNoHighSuffix(value);
        break;
      case "streetName":
        setStreetName(value);
        break;
      case "extension":
        setExtension(value.substring(0, 2).toUpperCase().replace(/[^A-Z]/g, ""));
        break;
      case "cornerExtension":
        setCornerExtension(value);
        break;
      case "floorUnit":
        setFloorUnit(value.substring(0, 5));
        break;
      case "cornerStreetName":
        setCornerStreetName(value);
        break;
      case "cityscopeReference":
        setCityscopeReference(value);
        break;
      case "lotPlan":
        setLotPlan(value);
        break;
      case "titleReference":
        setTitleReference(value);
        break;
      default:
        break;
    }

    setModified(true);
  };

  const handleSuburbChange = (selectedSuburb) => {
    setSelectedSuburb(selectedSuburb);
    if (selectedSuburb) {
      setSuburbId(selectedSuburb.value);
      setSuburb(selectedSuburb.label);
      setStateCode(selectedSuburb.state_code);
      setPostcode(selectedSuburb.postcode);
      setRegionName(selectedSuburb.region_name);
      setSubRegionName(selectedSuburb.sub_region_name);
    } else {
      setSuburbId(null);
      setSuburb("");
      setStateCode("");
      setPostcode("");
      setRegionName("");
      setSubRegionName("");
    }
    setModified(true);
  };

  const handleExtensionChange = (selectedExtension) => {
    setSelectedExtension(selectedExtension);
    if (selectedExtension) {
      setExtension(selectedExtension.value);
      setExtensionLabel(selectedExtension.label);
    } else {
      setExtension("");
      setExtensionLabel("");
    }
    setModified(true);
  };

  const handleCornerExtensionChange = (selectedCornerExtension) => {
    setSelectedCornerExtension(selectedCornerExtension);
    if (selectedCornerExtension) {
      setCornerExtension(selectedCornerExtension.value);
      setCornerExtensionLabel(selectedCornerExtension.label);
    } else {
      setCornerExtension("");
      setCornerExtensionLabel("");
    }
    setModified(true);
  };

  const loadSuburbOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      return [];
    }

    try {
      const response = await get({
        apiName: "colin",
        path: "/slmsuburbs",
        options: { queryParams: { suburb: inputValue } },
      }).response;
      const suburbs = await response.body.json();
      return suburbs || [];
    } catch (error) {
      console.error("Error loading suburb options:", error);
      return [];
    }
  };

  const handleSuburbInputChange = (newValue) => newValue;

  const validateBeforeSave = () => {
    if (!streetName || !streetName.trim()) {
      return "You must enter a street name";
    }

    if (!extension || !String(extension).trim()) {
      return "You must enter a street extension";
    }

    if (!suburbId) {
      return "You must select a suburb";
    }

    if (!stateCode) {
      return "You must select a state";
    }

    return "";
  };

  const saveAddress = async ({ showLoader = false } = {}) => {
    try {
      if (showLoader) {
        setIsLoading(true);
      }

      const validationMessage = validateBeforeSave();
      if (validationMessage) {
        setErrorMessage(validationMessage);
        showAlertState(true);
        return;
      }

      const payload = {
        address_id: addressRecordId || (addressId !== "new" ? Number(addressId) : null),
        address_key: addressKey,
        hard_sequence: hardSequence,
        building_name: buildingName,
        street_address_full: streetAddressFull,
        street_address: streetAddress,
        floor_unit: floorUnit,
        street_no_low: streetNoLow,
        street_no_low_suffix: streetNoLowSuffix,
        street_no_high: streetNoHigh,
        street_no_high_suffix: streetNoHighSuffix,
        street_name: streetName,
        extension,
        extension_label: extensionLabel,
        corner_street_name: cornerStreetName,
        corner_extension: cornerExtension,
        corner_extension_label: cornerExtensionLabel,
        suburb,
        suburb_id: suburbId,
        state_code: stateCode,
        postcode,
        sub_region_name: subRegionName,
        region_name: regionName,
        cityscope_reference: cityscopeReference,
        lot_plan: lotPlan,
        title_reference: titleReference,
        created_date: createdDate,
        created_by: createdBy,
        last_edited_by: user,
        archived_date: archivedDate,
      };

      let response;
      if (addressId === "new" && !hardSequence) {
        // Create new address
        response = await post({
          apiName: "colin",
          path: "/slmaddresses/new",
          options: { body: payload },
        }).response;
      } else {
        // Update existing address
        const updateId = addressRecordId || addressId;
        response = await put({
          apiName: "colin",
          path: `/slmaddresses/${updateId}`,
          options: { body: payload },
        }).response;
      }

      const result = await response.body.json();

      if (result.result === false) {
        setErrorMessage(result.error_message || "Error saving address");
        showAlertState(true);
      } else {
        setModified(false);
        if (addressId === "new") {
          navigate(`/slmaddresses/${result.id}`);
        }
      }
    } catch (error) {
      setErrorMessage(error.message || "Error saving address");
      showAlertState(true);
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  };

  const handleFieldBlur = async () => {
    if (!modified) {
      return;
    }
    await saveAddress();
  };

  const handleBack = () => {
    if (leaseId) {
      navigate(`/slmleases/${leaseId}`);
    } else if (saleId) {
      navigate(`/slmsales/${saleId}`);
    } else {
      navigate("/slmaddresses");
    }
  };

  const navigateToBrowseRecord = (targetId) => {
    navigate(`/slmaddresses/${targetId}`, { state: { data: idList } });
  };

  const formatDate = (value) => {
    if (!value) {
      return "";
    }
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) {
      return "";
    }
    return dt.toLocaleDateString("en-GB").replace(/\//g, "-");
  };

  const formatInteger = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return "0";
    }
    return new Intl.NumberFormat("en-AU", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parsed);
  };

  const formatCurrencyNoCents = (value) => {
    const parsed = Number(String(value || "0").replaceAll(",", "").replace("$", ""));
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number.isFinite(parsed) ? parsed : 0);
  };

  const columnHelper = createColumnHelper();

  const soldColumns = [
    columnHelper.accessor("sale_id", {
      id: "sale_id",
      header: "Sale ID",
      cell: (info) => <a href={`/slmsales/${info.getValue()}`}>{info.getValue()}</a>,
      size: 70,
    }),
    columnHelper.accessor("title_basis", { id: "title_basis", header: "Title Basis", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("sale_type", { id: "sale_type", header: "Type", cell: (info) => info.getValue() || "", size: 80 }),
    columnHelper.accessor("stat_date", {
      id: "stat_date",
      header: "Stat Date",
      cell: (info) => formatDate(info.getValue()),
      size: 90,
    }),
    columnHelper.accessor("note_data", {
      id: "note_data",
      header: "Sales Notes",
      cell: (info) => <Form.Control readOnly as="textarea" rows={1} value={info.getValue() || ""} />,
      size: 180,
    }),
    columnHelper.accessor("sale_price", {
      id: "sale_price",
      header: "Price",
      cell: (info) => info.getValue() || formatCurrencyNoCents(0),
      size: 100,
    }),
    columnHelper.accessor("floor_area", {
      id: "floor_area",
      header: "BldgArea",
      cell: (info) => formatInteger(info.getValue()),
      size: 90,
    }),
    columnHelper.accessor((row) => {
      const price = Number(String(row.sale_price || "0").replaceAll(",", "").replace("$", ""));
      const area = Number(row.floor_area || 0);
      return area > 0 ? price / area : 0;
    }, {
      id: "price_per_sqm",
      header: "Price Per SQM",
      cell: (info) => formatCurrencyNoCents(info.getValue()),
      size: 110,
    }),
    columnHelper.accessor("broad_space_use_code", { id: "broad_space_use_code", header: "Broad Use", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("space_use_name", { id: "space_use_name", header: "Space Use", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("agent", { id: "agent", header: "Agent", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("agency", { id: "agency", header: "Agency", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("purchaser", { id: "purchaser", header: "Buyer", cell: (info) => info.getValue() || "" }),
    columnHelper.display({
      id: "clone",
      header: "",
      cell: (info) => <Button variant="primary" size="sm" type="button" onClick={() => navigate("/slmsales/new", { state: { clone: info.row.original } })}>Clone</Button>,
      size: 80,
    }),
  ];

  const leaseColumns = [
    columnHelper.accessor("lease_id", {
      id: "lease_id",
      header: "Lease ID",
      cell: (info) => <a href={`/slmleases/${info.getValue()}`}>{info.getValue()}</a>,
      size: 70,
    }),
    columnHelper.accessor("lease_level", { id: "lease_level", header: "Suite / Level", cell: (info) => info.getValue() || "", size: 90 }),
    columnHelper.accessor("lease_type", { id: "lease_type", header: "Type", cell: (info) => info.getValue() || "", size: 80 }),
    columnHelper.accessor("stat_date", {
      id: "stat_date",
      header: "Stat Date",
      cell: (info) => formatDate(info.getValue()),
      size: 90,
    }),
    columnHelper.accessor("note_data", {
      id: "note_data",
      header: "Lease Notes",
      cell: (info) => <Form.Control readOnly as="textarea" rows={1} value={info.getValue() || ""} />,
      size: 180,
    }),
    columnHelper.accessor((row) => {
      const annual = Number(row.rent_per_annum || 0);
      const area = Number(row.tenant_area || 0);
      return area > 0 ? annual / area : 0;
    }, {
      id: "rent_per_sqm",
      header: "Rent Per SQM",
      cell: (info) => formatCurrencyNoCents(info.getValue()),
      size: 110,
    }),
    columnHelper.accessor("tenant_area", {
      id: "tenant_area",
      header: "Tenant Area",
      cell: (info) => formatInteger(info.getValue()),
      size: 90,
    }),
    columnHelper.accessor("rent_per_annum", {
      id: "rent_per_annum",
      header: "Rent Per Annum",
      cell: (info) => formatCurrencyNoCents(info.getValue()),
      size: 120,
    }),
    columnHelper.accessor("broad_space_use_code", { id: "broad_space_use_code", header: "Broad Use", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("space_use_name", { id: "space_use_name", header: "Space Use", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("agent", { id: "agent", header: "Agent", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("agency", { id: "agency", header: "Agency", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor("lessee", { id: "lessee", header: "Lessee", cell: (info) => info.getValue() || "" }),
    columnHelper.display({
      id: "clone",
      header: "",
      cell: (info) => <Button variant="primary" size="sm" type="button" onClick={() => navigate("/slmleases/new", { state: { clone: info.row.original } })}>Clone</Button>,
      size: 80,
    }),
  ];

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="SLMAddressDetail">
      {isLoading && (
        <div className="outer-div">
          <div className="sweet-loading inner-div">
            <PacmanLoader
              cssOverride={{ display: "block", margin: "0 auto" }}
              size={20}
              color={"#e03c31"}
              loading={isLoading}
            />
          </div>
        </div>
      )}

      {!isLoading && (
        <Form>
          {showAlert && (
            <Modal show={true} onHide={() => showAlertState(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>Error</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Alert variant="danger">{errorMessage}</Alert>
              </Modal.Body>
            </Modal>
          )}

          <Row className="mb-3 align-items-center">
            <Col xs={12} lg={5}>
              <h6>
                Address Details - {(buildingName || "")} | {(streetAddress || "")} {(suburb || "")} {(stateCode || "")} {(postcode || "")} {archivedDate && <span style={{ color: "red" }}>|| *** ARCHIVED ***</span>}
              </h6>
            </Col>
            <Col xs={12} lg={3} className="text-lg-center text-start mt-2 mt-lg-0">
              <SLMBrowsePager idList={idList} currentRecordId={addressId} onNavigate={navigateToBrowseRecord} />
            </Col>
            <Col xs={12} lg={4} className="text-lg-end text-start mt-2 mt-lg-0">
              <Button variant="primary" size="sm" type="button" onClick={handleBack}>Back to Address Search</Button>
            </Col>
          </Row>

          {addressId !== "new" && (
            <Row className="mb-2">
              <Col lg={1} />
              <Col>
                <span style={{ color: "red" }}>Please Note: Edits made to this address will apply to ALL transactions linked to this address</span>
              </Col>
            </Row>
          )}

          <Row>
            <Col lg={3} md={12} sm={12}>
              <Form.Group as={Row} className="mb-2 align-items-center">
                <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>CoLin ID</Form.Label>
                <Col style={{ paddingRight: 5 }}>
                  <Form.Control disabled readOnly value={addressRecordId || ""} type="text" style={{ paddingRight: 5 }} />
                </Col>
                <Form.Label
                  as={Col}
                  column
                  lg={2}
                  md={4}
                  sm={4}
                  className="text-right mt-2 mt-lg-0"
                  style={{ paddingRight: 1, paddingLeft: 1 }}
                >
                  PIMs ID
                </Form.Label>
                <Col lg={3} md={3} sm={4} style={{ paddingLeft: 5 }}>
                  <Form.Control disabled readOnly value={hardSequence || ""} type="text" />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Building Name</Form.Label>
                <Col>
                  <Form.Control id="buildingName" value={buildingName} type="text" onChange={handleChange} onBlur={handleFieldBlur} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Street Number Low</Form.Label>
                <Col lg={5} sm={4}>
                  <Form.Control id="streetNoLow" value={streetNoLow} type="text" onChange={handleChange} onBlur={handleFieldBlur} />
                </Col>
                <Col lg={3} sm={3}>
                  <Form.Control id="streetNoLowSuffix" value={streetNoLowSuffix} type="text" onChange={handleChange} onBlur={handleFieldBlur} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Street Number High</Form.Label>
                <Col lg={5} sm={4}>
                  <Form.Control id="streetNoHigh" value={streetNoHigh} type="text" onChange={handleChange} onBlur={handleFieldBlur} />
                </Col>
                <Col lg={3} sm={3}>
                  <Form.Control id="streetNoHighSuffix" value={streetNoHighSuffix} type="text" onChange={handleChange} onBlur={handleFieldBlur} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Street Name</Form.Label>
                <Col>
                  <Form.Control id="streetName" value={streetName} type="text" onChange={handleChange} onBlur={handleFieldBlur} />
                </Col>
              </Form.Group>

              <Row className="mb-2">
                <Col lg={{ span: 8, offset: 4 }} sm={{ span: 7, offset: 5 }}>
                  <small>Note: Street Name MUST exclude Street Extension</small>
                </Col>
              </Row>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Street Extension</Form.Label>
                <Col lg={5} sm={4}>
                  <Select value={selectedExtension} onChange={handleExtensionChange} onBlur={handleFieldBlur} options={streetTypes} isClearable />
                </Col>
                <Col lg={3} sm={3}>
                  <Form.Control readOnly value={selectedExtension?.value || ""} type="text" />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">cnr Street Name</Form.Label>
                <Col>
                  <Form.Control id="cornerStreetName" value={cornerStreetName} type="text" onChange={handleChange} onBlur={handleFieldBlur} />
                </Col>
              </Form.Group>

              <Row className="mb-2">
                <Col lg={{ span: 8, offset: 4 }} sm={{ span: 7, offset: 5 }}>
                  <small>Note: Street Name MUST exclude Street Extension</small>
                </Col>
              </Row>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">cnr Street Extension</Form.Label>
                <Col lg={5} sm={4}>
                  <Select value={selectedCornerExtension} onChange={handleCornerExtensionChange} onBlur={handleFieldBlur} options={streetTypes} isClearable />
                </Col>
                <Col lg={3} sm={3}>
                  <Form.Control readOnly value={selectedCornerExtension?.value || ""} type="text" />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Display Address</Form.Label>
                <Col>
                  <Form.Control disabled value={streetAddress} type="text" />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Suburb</Form.Label>
                <Col>
                  <AsyncSelect
                    value={selectedSuburb}
                    cacheOptions
                    defaultOptions={suburbOptions}
                    loadOptions={loadSuburbOptions}
                    onInputChange={handleSuburbInputChange}
                    onChange={handleSuburbChange}
                    onBlur={handleFieldBlur}
                    isClearable
                    placeholder="Type 3 or more characters to search suburbs"
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">State</Form.Label>
                <Col>
                  <Form.Control readOnly value={stateCode || ""} type="text" />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Postcode</Form.Label>
                <Col>
                  <Form.Control readOnly value={postcode || ""} type="text" />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Statistical Division</Form.Label>
                <Col>
                  <Form.Control readOnly value={regionName || ""} type="text" />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">SLM4 Street Address</Form.Label>
                <Col>
                  <Form.Control disabled value={originStreetAddress || ""} type="text" />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Created Date</Form.Label>
                <Col>
                  <Form.Control plaintext readOnly value={formatDate(createdDate)} />
                </Col>
                <Col xs="auto">
                  <RefreshButton
                    onClick={refreshAddressMatch}
                    onTimeout={reloadTransactions}
                    timeout={90000}
                    size="sm"
                    variant="secondary"
                    type="button"
                    disabled={addressId === "new" || !addressRecordId}
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Created By</Form.Label>
                <Col>
                  <Form.Control plaintext readOnly value={createdBy || ""} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Last Edited Date</Form.Label>
                <Col>
                  <Form.Control plaintext readOnly value={formatDate(lastEditedDate)} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label as={Col} lg={4} sm={5} className="text-lg-end">Last Edited By</Form.Label>
                <Col>
                  <Form.Control plaintext readOnly value={lastEditedBy || ""} />
                </Col>
              </Form.Group>

            </Col>

            <Col lg={9} md={12} sm={12} className="mt-4 mt-lg-0">
              <Row className="mb-1 align-items-center g-2">
                <Col xs="auto" style={{ width: "72px" }} />
                <Col className="text-center">
                  <h5 className="mb-0">SOLD/For Sale Transactions</h5>
                </Col>
                <Col xs="auto" className="text-end">
                  <Button variant="primary" size="sm" type="button" onClick={() => navigate("/slmsales/new")}>Add</Button>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col><b>{sales.length} of {sales.length > 0 ? (sales[0].result_count || sales.length) : 0} records returned.</b></Col>
              </Row>
              <GenericTable
                columns={soldColumns}
                data={sales || []}
                pageSize={PAGE_SIZES.slmAddressDetailSold}
                enablePagination={true}
                hasHeaderSorting={true}
                section="slm-address-detail-sold"
                enforceMinRowsHeight={true}
              />

              <Row className="mt-4 mb-1 align-items-center g-2">
                <Col xs="auto" style={{ width: "72px" }} />
                <Col className="text-center">
                  <h5 className="mb-0">LEASED/For Lease Transactions</h5>
                </Col>
                <Col xs="auto" className="text-end">
                  <Button variant="primary" size="sm" type="button" onClick={() => navigate("/slmleases/new")}>Add</Button>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col><b>{leases.length} of {leases.length > 0 ? (leases[0].result_count || leases.length) : 0} records returned.</b></Col>
              </Row>
              <GenericTable
                columns={leaseColumns}
                data={leases || []}
                pageSize={PAGE_SIZES.slmAddressDetailLease}
                enablePagination={true}
                hasHeaderSorting={true}
                section="slm-address-detail-lease"
                enforceMinRowsHeight={true}
              />
            </Col>
          </Row>
        </Form>
      )}
    </div>
  );
}

export default SLMAddressDetail;
